import {
	RecorderError,
	toPermission,
	type VoiceRecorder,
} from "./voice-recorder.base"
import {
	keepMicStream,
	stopTracks,
	takeMicStream,
} from "./voice-recorder.mic-stream"

const WEB_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
	channelCount: { ideal: 1 },
	echoCancellation: false,
	noiseSuppression: true,
	autoGainControl: true,
}

const WEB_BIT_RATE = 32_000
const PERMISSION_TIMEOUT_MS = 30_000

function pickWebMimeType(): string | undefined {
	if (typeof MediaRecorder === "undefined") return undefined
	if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4"
	if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
		return "audio/webm;codecs=opus"
	}
	return undefined
}

function webFileName(mimeType: string): string {
	if (mimeType.includes("mp4")) return "voice.m4a"
	if (mimeType.includes("webm")) return "voice.webm"
	if (mimeType.includes("ogg")) return "voice.ogg"
	return "voice.bin"
}

function isPermissionDenial(error: unknown): boolean {
	return (
		error instanceof Error &&
		(error.name === "NotAllowedError" || error.name === "SecurityError")
	)
}

interface Session {
	stream: MediaStream
	recorder: MediaRecorder
	chunks: Blob[]
	startedAt: number | null
	closed: boolean
	detachTrack: (() => void) | null
}

function withTimeout(pending: Promise<MediaStream>): Promise<MediaStream> {
	return new Promise<MediaStream>((resolve, reject) => {
		let timedOut = false
		const timer = setTimeout(() => {
			timedOut = true
			reject(new RecorderError("timeout"))
		}, PERMISSION_TIMEOUT_MS)
		pending.then(
			(stream) => {
				clearTimeout(timer)
				if (timedOut) stopTracks(stream)
				else resolve(stream)
			},
			(error: unknown) => {
				clearTimeout(timer)
				reject(error)
			},
		)
	})
}

async function openStream(): Promise<MediaStream> {
	if (!navigator.mediaDevices?.getUserMedia) {
		throw new RecorderError("unavailable")
	}
	const reused = takeMicStream()
	if (reused !== null) return reused
	try {
		return await withTimeout(
			navigator.mediaDevices.getUserMedia({ audio: WEB_AUDIO_CONSTRAINTS }),
		)
	} catch (error) {
		if (error instanceof RecorderError) throw error
		if (isPermissionDenial(error)) {
			throw new RecorderError("permission", { cause: error })
		}
		throw new RecorderError("unavailable", { cause: error })
	}
}

function createMediaRecorder(stream: MediaStream): MediaRecorder {
	const options: MediaRecorderOptions = { audioBitsPerSecond: WEB_BIT_RATE }
	const mimeType = pickWebMimeType()
	if (mimeType) options.mimeType = mimeType
	try {
		return new MediaRecorder(stream, options)
	} catch (error) {
		throw new RecorderError("unavailable", { cause: error })
	}
}

function startMediaRecorder(recorder: MediaRecorder): Promise<void> {
	const listeners = new AbortController()
	return new Promise<void>((resolve, reject) => {
		recorder.addEventListener("start", () => resolve(), {
			signal: listeners.signal,
		})
		recorder.addEventListener(
			"error",
			() => reject(new RecorderError("unavailable")),
			{ signal: listeners.signal },
		)
		try {
			recorder.start()
		} catch (error) {
			reject(new RecorderError("unavailable", { cause: error }))
		}
	}).finally(() => listeners.abort())
}

function stopMediaRecorder(recorder: MediaRecorder): Promise<void> {
	return new Promise<void>((resolve) => {
		recorder.addEventListener("stop", () => resolve(), { once: true })
		recorder.stop()
	})
}

export function createWebRecorder(): VoiceRecorder {
	const interruptionListeners = new Set<() => void>()

	let session: Session | null = null

	function close(target: Session | null, keep: boolean): void {
		if (target === null || target.closed) return
		target.closed = true
		target.detachTrack?.()
		if (session === target) session = null
		if (keep) keepMicStream(target.stream)
		else stopTracks(target.stream)
	}

	return {
		async checkPermission() {
			if (!navigator.permissions?.query) {
				return "prompt"
			}
			try {
				const result = await navigator.permissions.query({
					name: "microphone" as PermissionName,
				})
				return toPermission(result.state)
			} catch {
				return "prompt"
			}
		},

		async requestPermission() {
			return "granted"
		},

		async start() {
			close(session, false)
			if (typeof MediaRecorder === "undefined") {
				throw new RecorderError("unavailable")
			}

			const stream = await openStream()
			const track = stream.getAudioTracks()[0]
			if (!track) {
				stopTracks(stream)
				throw new RecorderError("noTrack")
			}

			let recorder: MediaRecorder
			try {
				recorder = createMediaRecorder(stream)
			} catch (error) {
				stopTracks(stream)
				throw error
			}

			const current: Session = {
				stream,
				recorder,
				chunks: [],
				startedAt: null,
				closed: false,
				detachTrack: null,
			}
			session = current

			const interrupt = () => {
				if (session !== current) return
				for (const listener of interruptionListeners) {
					listener()
				}
			}
			track.addEventListener("ended", interrupt)
			current.detachTrack = () => {
				track.removeEventListener("ended", interrupt)
			}
			recorder.addEventListener("error", interrupt)
			recorder.addEventListener("dataavailable", (event) => {
				if (event.data.size > 0) {
					current.chunks.push(event.data)
				}
			})

			try {
				await startMediaRecorder(recorder)
			} catch (error) {
				close(current, false)
				throw error
			}

			if (track.readyState !== "live") {
				close(current, false)
				throw new RecorderError("noTrack")
			}

			current.startedAt = Date.now()
		},

		async stop() {
			const current = session
			if (current === null || current.startedAt === null) {
				close(current, false)
				throw new RecorderError("noData")
			}

			if (current.recorder.state !== "inactive") {
				await stopMediaRecorder(current.recorder)
			}

			const durationMs = Date.now() - current.startedAt
			const mimeType =
				current.recorder.mimeType || pickWebMimeType() || "audio/webm"
			close(current, current.chunks.length > 0)

			if (current.chunks.length === 0) {
				throw new RecorderError("noData")
			}

			return {
				blob: new Blob(current.chunks, { type: mimeType }),
				durationMs,
				fileName: webFileName(mimeType),
			}
		},

		async cancel() {
			const current = session
			if (current === null) return
			if (current.recorder.state !== "inactive") {
				try {
					current.recorder.stop()
				} catch {}
			}
			close(current, true)
		},

		onInterruption(listener) {
			interruptionListeners.add(listener)
			return () => {
				interruptionListeners.delete(listener)
			}
		},
	}
}
